import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import { getDateRangeFilterOperator, getRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import { DataGrid, PageTitleWithToolTip } from '../Common';
import { StyledCellLink, Weight, Percentage, BadgeWithLinkedText } from '../Common/DataGrid/CellComponents';
import { renderStatusBadge } from '../Common/Table/utils';
import { select_commonCellProps, text_commonCellProps } from '../Common/DataGrid/utils';
import { Box, CardHeader, Grid } from '@mui/material';
import { useState } from 'react';
import { useEffect } from 'react';
import { fetchFilterValues } from '../../utils';


const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});

const TechnicianServiceHistory = () => {
    const [filters, setFilters] = useState([]);

    const fetchFilters = async () => {
        const filters = await fetchFilterValues(Routes.WorkItem.TableFilters());
        setFilters(filters);
    };

    useEffect(() => {
        fetchFilters();
    }, []);

    const columns = [
        {
            field: 'idString',
            headerName: 'Service Event ID',
            ...text_commonCellProps,
            renderCell: ({ row: { workItemStatus, id, idString } }) => {                
                if (!UserInfo.isAdminRole() && workItemStatus?.toLowerCase() === 'deleted') {
                    return (
                        <>
                            {renderStatusBadge(workItemStatus)}
                            <span>{idString}</span>
                        </>
                    );
                }
                return (
                    <BadgeWithLinkedText
                        statusProperty={workItemStatus}
                        text={idString}
                        to={{
                            pathname: `/ServiceEvent/${id}`,
                            state: {
                                from: "Service Events"
                            }
                        }}
                    />
                );
            }
        },
        {
            field: 'region',
            headerName: 'Region',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'locationName',
            headerName: 'Location',
            ...select_commonCellProps,
            // US 16741 (QA): ungated like the other Location columns app-wide —
            // the old hasNoPermission gate was wired inverted (HomePage passes
            // userPermission), hiding the link from Technician/Tech Manager.
            renderCell: ({ row: { locationId, locationName } }) => (
                <StyledCellLink
                    path={`/Location/${locationId}`}
                    text={locationName}
                    from={"Dashboard"}
                />
            )
        },
        {
            field: 'locationCode',
            headerName: 'Location Code',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'city',
            headerName: 'City',
            ...select_commonCellProps
        },
        {
            field: 'locationState',
            headerName: 'State',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'workOrderId',
            headerName: 'Work Order ID',
            ...text_commonCellProps,
            renderCell: ({ row: { workOrderId } }) => {
                return (
                    <StyledCellLink
                        path={`/WorkOrder/${workOrderId}`}
                        text={workOrderId}
                        from={"Dashboard"}
                        primaryLink
                    />
                );
            }
        },
        {
            field: 'workOrderNumber',
            headerName: 'Customer Work Order #',
            ...text_commonCellProps
        },
        {
            field: 'referenceNumber',
            headerName: 'Reference Number',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'workItemType',
            headerName: 'Type',
            ...select_commonCellProps
        },
        {
            field: 'serviceDate',
            headerName: 'Service Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'workItemStatus',
            headerName: 'Status',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'primaryTechnicianName',
            headerName: 'Primary Technician',
            ...select_commonCellProps
        },
        {
            field: 'assetName',
            headerName: 'Asset Name',
            ...select_commonCellProps,
            renderCell: ({ row: { assetName, assetId } }) => (
                <StyledCellLink
                    path={`/Inventory/${assetId}`}
                    text={assetName}
                    from={"Dashboard"}
                />
            )
        },
        {
            field: 'servicerName',
            headerName: 'Service Provider',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'netGasLbsAdded',
            headerName: 'Net Gas Added',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            renderCell: ({ row: { netGasLbsAdded } }) => <Weight value={netGasLbsAdded} />
        },
        {
            field: 'leakRatio',
            headerName: 'Per Occurrence Leak Rate %',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            renderCell: ({ row: { leakRatio } }) => <Percentage value={leakRatio} />
        },
        {
            field: 'systemManager',
            headerName: 'System Manager',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'classification',
            headerName: 'Classification',
            initiallyVisible: false,
            ...text_commonCellProps,
        }
    ];

    return (
        <>
            <PageTitleWithToolTip
                pageTitle="Your Service History"
                toolTipText
                fontSize="18px"
                sx={{
                    marginBottom: "14px",
                    marginLeft: { xs: "16px", md: 0 },
                    display: { xs: 'block', lg: 'none' }
                }}
            />
            <DataGrid
                    url={Routes.WorkItem.WorkItemTableTech()}
                    filterOptions={filters}
                    exportButtonURL={Routes.WorkItem.TableTechExport()}
                    exportButtonAlt={"Export Service History (.csv)"}
                    exportFileName={"TechnicianServiceHistory"}
                    noResultText="No Service Event History"
                    noResultText2="Service events will appear here when assigned to you."
                    columns={columns}
                    rowCount={5}
                    disablePagination={false}
                    dataGridSx={{
                        '& .MuiButton-root[aria-label*="Export"]': {
                            display: { xs: 'none', lg: 'inline-flex' }
                        }
                    }}
                    headerContent={
                        <CardHeader
                            title={
                                <PageTitleWithToolTip
                                    pageTitle="Your Service History"
                                />
                            }
                            sx={{
                                "& .MuiCardHeader-action": { display: "flex" },
                                "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" },
                                display: { xs: 'none', lg: 'block' }
                            }}
                        />
                    }
                />
        </>
    );
};

export default TechnicianServiceHistory;
